import { Link } from 'react-router-dom';

import ActivityBadge from '@/components/ui/activity-badge';
import { SortTh } from '@/components/ui/sort-th';
import { table } from '@/components/ui/styles';
import type { Activity, CasinoCard } from '@/types/api';
import { countryName, formatCount, formatDate, formatMoneyFull } from '@/utils/format';
import { casinoStatusLabel } from '@/utils/labels';
import { dateValue, type Sort, type SortColumns } from '@/utils/use-sort';

// Activity sorts from the most active down, not A to Z.
const ACTIVITY_RANK: Record<Activity, number> = { high_velocity: 3, active: 2, dormant: 1, not_live: 0 };

// The owner's email and the status (which has its own filter) do not sort: their order means nothing.
export const CASINO_SORT: SortColumns<CasinoCard> = {
  name: { value: (c) => c.brand_name },
  country: { value: (c) => (c.country ? countryName(c.country) : '') },
  activity: { value: (c) => ACTIVITY_RANK[c.activity], desc: true },
  ggr: { value: (c) => c.metrics?.ggr_30d_usd, desc: true },
  players: { value: (c) => c.metrics?.players_30d, desc: true },
  velocity: { value: (c) => c.metrics?.velocity, desc: true },
  live: { value: (c) => dateValue(c.launched_at), desc: true },
};

/** Casinos start the busiest first, in the table and the cards alike (2026-11-03). */
export const CASINO_DEFAULT_SORT: Sort = { key: 'velocity', desc: true };

/** What the page sorts by, one entry per column the table sorts by, and what each direction means. */
export const CASINO_SORT_OPTIONS: { key: string; label: string; desc: string; asc: string }[] = [
  { key: 'velocity', label: 'Velocity', desc: 'highest first', asc: 'lowest first' },
  { key: 'ggr', label: 'GGR', desc: 'highest first', asc: 'lowest first' },
  { key: 'players', label: 'Players', desc: 'most first', asc: 'fewest first' },
  { key: 'activity', label: 'Activity', desc: 'most active first', asc: 'least active first' },
  { key: 'live', label: 'Live since', desc: 'newest first', asc: 'oldest first' },
  { key: 'name', label: 'Casino', desc: 'Z to A', asc: 'A to Z' },
  { key: 'country', label: 'Country', desc: 'Z to A', asc: 'A to Z' },
];

/** The same casinos as the cards, as a table: for comparing, and as the cards' accessible twin. The page sorts them. */
const CasinoTable = ({ rows, sort, onSort: toggle }: { rows: CasinoCard[]; sort: Sort | null; onSort: (key: string) => void }) => {
  return (
    <div className={table.wrap}>
      <div className="overflow-x-auto">
        <table className={`${table.table} min-w-[900px]`}>
          <thead className={table.head}>
            <tr>
              <SortTh label="Casino" column="name" sort={sort} onSort={toggle} />
              <th scope="col" className={table.th}>Owner</th>
              <SortTh label="Country" column="country" sort={sort} onSort={toggle} />
              <th scope="col" className={table.th}>Status</th>
              <SortTh label="Activity" column="activity" sort={sort} onSort={toggle} />
              <SortTh label="GGR, 30 days" column="ggr" sort={sort} onSort={toggle} right />
              <SortTh label="Players" column="players" sort={sort} onSort={toggle} right />
              <SortTh label="Velocity" column="velocity" sort={sort} onSort={toggle} right />
              <SortTh label="Live since" column="live" sort={sort} onSort={toggle} />
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className={table.row}>
                <td className={table.td}>
                  <Link to={`/casinos/${encodeURIComponent(c.id)}`} className="text-fg-strong hover:text-brand hover:underline">
                    {c.brand_name || 'Unnamed casino'}
                  </Link>
                  <p className="text-xs text-fg-muted">{c.domain || '—'}</p>
                </td>
                <td className={table.td}>
                  {c.lead_id ? (
                    <Link to={`/leads?lead=${c.lead_id}`} className="text-fg hover:text-brand hover:underline">
                      {c.owner_email}
                    </Link>
                  ) : (
                    <span className="text-fg-faint">Not linked</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-fg">{c.country ? countryName(c.country) : '—'}</td>
                <td className="whitespace-nowrap px-4 py-3 text-fg">{casinoStatusLabel(c.status)}</td>
                <td className={table.td}>
                  <ActivityBadge activity={c.activity} />
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-fg">{c.metrics ? formatMoneyFull(c.metrics.ggr_30d_usd) : '—'}</td>
                <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-fg">{c.metrics ? formatCount(c.metrics.players_30d) : '—'}</td>
                <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-fg">{c.metrics ? c.metrics.velocity.toFixed(1) : '—'}</td>
                <td className="whitespace-nowrap px-4 py-3 tabular-nums text-fg-muted">{c.launched_at ? formatDate(c.launched_at) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CasinoTable;
